// Pop-up for the notifications people can't afford to miss in the bell: an @mention,
// a document added to a job they're on, a new job assignment, a to-do handed to them,
// and a client writing back by text or through the portal (asked 2026-10-24 — the
// bell alone was getting overlooked). Emails matched to jobs stay bell-only: at ~48
// per person a month they'd turn the pop-up into noise.
//
// Whether something has been seen is the notification's own is_read flag, so it
// clears everywhere: "Got it" or "Open" marks it read, and it never pops again on
// the phone or the desktop. Anything left unread pops again on the next load.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getNotifications, markNotificationRead } from '../services/api';

const POPUP_TYPES = {
  mention: { icon: '💬', label: 'Mentioned you' },
  document: { icon: '📄', label: 'New document' },
  assignment: { icon: '📋', label: 'Assigned to you' },
  todo_assigned: { icon: '✅', label: 'New to-do' },
  sms_reply: { icon: '📱', label: 'Client text' },
  client_request: { icon: '🙋', label: 'Client request' },
};

// Older unread notices stay in the bell; only recent ones are worth interrupting for
// (and the first load after this shipped shouldn't dump a backlog on anyone).
const MAX_AGE_MS = 3 * 24 * 60 * 60 * 1000;
const POLL_MS = 30000;

const parseUtc = (iso) => new Date(iso && !iso.endsWith('Z') && !iso.includes('+') ? `${iso}Z` : iso);

const timeAgo = (iso) => {
  const mins = Math.floor((Date.now() - parseUtc(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

function ImportantNotificationPopup({ blocked }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  // Ids already acted on this session, so a failed mark-read (offline) doesn't
  // bring the same card straight back on the next poll.
  const handled = useRef(new Set());

  const load = useCallback(async () => {
    try {
      const res = await getNotifications();
      const now = Date.now();
      const fresh = (res.data.notifications || []).filter(n =>
        !n.is_read && POPUP_TYPES[n.notification_type] && !handled.current.has(n.id)
        && now - parseUtc(n.created_at).getTime() < MAX_AGE_MS);
      setItems(fresh);
    } catch (e) {
      // Offline or signed out: try again on the next poll.
    }
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    load();
    const interval = setInterval(load, POLL_MS);
    const onFocus = () => load();
    // Phones don't reliably fire window 'focus' when you switch back to the app,
    // but they do fire visibilitychange, so check then too instead of waiting for
    // the next 30-second poll.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user, load]);

  const markRead = (ids) => {
    ids.forEach(id => {
      handled.current.add(id);
      markNotificationRead(id).catch(() => {});
    });
    setItems(prev => prev.filter(n => !ids.includes(n.id)));
  };

  const open = (n) => {
    markRead([n.id]);
    if (n.notification_type === 'todo_assigned') navigate('/todos');
    else if (n.related_job_id) navigate(`/jobs/${n.related_job_id}`);
  };

  if (!user || blocked || items.length === 0) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 100000, padding: '16px',
    }}>
      <div style={{
        backgroundColor: 'white', borderRadius: '14px', width: '100%', maxWidth: '460px',
        maxHeight: 'calc(90dvh - 40px)', display: 'flex', flexDirection: 'column',
        overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      }}>
        <div style={{ padding: '16px 20px', backgroundColor: '#0f2942', color: 'white' }}>
          <div style={{ fontSize: '17px', fontWeight: 700 }}>
            🔔 {items.length === 1 ? 'You have a new notification' : `You have ${items.length} new notifications`}
          </div>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {items.map(n => {
            const kind = POPUP_TYPES[n.notification_type];
            const canOpen = n.notification_type === 'todo_assigned' || n.related_job_id;
            return (
              <div key={n.id} style={{
                display: 'flex', gap: '12px', padding: '14px 20px',
                borderBottom: '1px solid #f1f5f9', alignItems: 'flex-start',
              }}>
                <div style={{ fontSize: '22px', lineHeight: 1 }}>{kind.icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {kind.label} · {timeAgo(n.created_at)}
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>{n.title}</div>
                  {n.message && (
                    <div style={{ fontSize: '13px', color: '#475569', marginTop: '2px', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {n.message}
                    </div>
                  )}
                </div>
                {canOpen && (
                  <button
                    onClick={() => open(n)}
                    style={{
                      flexShrink: 0, padding: '8px 14px', backgroundColor: '#2563eb', color: 'white',
                      border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    Open
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={() => markRead(items.map(n => n.id))}
            style={{
              padding: '10px 22px', backgroundColor: '#0f2942', color: 'white', border: 'none',
              borderRadius: '8px', fontSize: '14px', fontWeight: 700, cursor: 'pointer',
            }}
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImportantNotificationPopup;
